import React, { useContext, useState, useEffect } from 'react';
import { Highlight, Prism, themes } from 'prism-react-renderer';
import Editor from 'react-simple-code-editor';
import ExportPopUp from './ExportPopUp';

import {
    CopilotService,
    type ChatSegmentRequest,
    ChatSegmentResponse,
    ChatScriptResponse,
    ExportSegmentRequest,
    MappedTitle,
} from '../../Service/ServerAPI';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, setScriptMessage } from '../../utils/store';
import { FolderArrowRight28Regular } from '@fluentui/react-icons';
import Results from './Results';
import { AppInsightsContext } from '../../utils/appInsights';
import { useUser } from '../../utils/UserContext';
import { canExportFromSession, EXPORT_DISABLED_NOT_SESSION_OWNER } from '../../utils/exportPermissions';
import { useTokenService } from '../../tokenService';
import {
    Button,
    Card,
    Link, 
    makeStyles, 
    Spinner,
    Subtitle2Stronger,
    Toaster, Toast, ToastBody, ToastIntent, ToastTitle, ToastTrigger, 
    tokens,
    useId,
    useToastController,
} from '@fluentui/react-components';
import {
    ExportType,
    getExportDataset,
    isExportFeatureEnabled,
} from '../../types/ExportTypes';


const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        marginBottom: tokens.spacingVerticalM,
    },
    innerContainer: {
        position: 'relative',
        width: '100%',
    },
    card: {
        color: '#fff',
        backgroundColor: '#3A3E45',
        maxHeight: '340px',
        overflow: 'auto',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: tokens.shadow16,
    },
    title: {
        borderBottom: '1px solid #6B7584',
        fontWeight: 'bold',
        height: '35px',
        fontFamily: 'Segoe UI',
        fontSize: '1rem',
    },
    button: {
        position: 'absolute' as 'absolute',
        top: '10px',
        right: '10px',
        backgroundColor: 'green',
        color: 'white',
        border: 'none',
        padding: '7px 15px',
        cursor: 'circle',
        borderRadius: '8px',
    },
});

interface ToastProps {
    type: ToastIntent | 'progress';
    message: string;
    description?: string;
    duration?: number;
    dismissable?: boolean;
}

interface LuaScriptMessageProps {
    chatUser: ChatSegmentRequest;
    chatInteraction: ChatSegmentResponse;
    preScriptResult?: ChatScriptResponse;
    showZeroCountBanner: (open: boolean) => void;
    messageIndex: number;
    messageSequenceIndex: number;
}
const LuaScriptMessage: React.FC<LuaScriptMessageProps> = ({
    chatUser,
    chatInteraction,
    preScriptResult,
    showZeroCountBanner,
    messageIndex,
    messageSequenceIndex
}) => {
    const [input, setInput] = React.useState(chatUser.userInput);
    const [pseudocode, setPseudocode] = React.useState(chatInteraction.segment);
    const [script, setScript] = React.useState(chatInteraction.script);
    //const [titlesMapped, settitlesMapped] = React.useState(MappedTitle[]);
    const [confidence, setConfidence] = React.useState(
        chatInteraction.confidence ?? ''
    );
    const [scriptResult, setScriptResult] =
        React.useState<ChatScriptResponse | null>(preScriptResult ?? null);
    const [isPseudocodeRunning, setPseudocodeRunning] = React.useState(false);
    const [isScriptRunning, setScriptRunning] = React.useState(false);

    // State variables used for the export process
    const [toastProps, setToastProps] = useState<ToastProps>({type: 'info', message: ''});
    const confirmationToastId= useId('confirmationToast');
    const { dispatchToast, dismissAllToasts } = useToastController(confirmationToastId);
    useEffect(function displayConfirmationToast() {
        dismissAllToasts();
        if (!toastProps.message) return;
        let toastContent = <></>;
        if (toastProps.type === 'progress') {
            toastContent = (
                <Toast>
                    {toastProps.dismissable ? 
                        <ToastTitle media={<Spinner size="tiny"/>} action={<ToastTrigger><Link>Dismiss</Link></ToastTrigger>}>
                            {toastProps.message}
                        </ToastTitle> :
                        <ToastTitle media={<Spinner size="tiny"/>}>{toastProps.message}</ToastTitle>}
                </Toast>
            );
        } else if (toastProps.type === 'error' || toastProps.type === 'info') {
            toastContent = (
                <Toast>
                    {toastProps.dismissable ? 
                        <ToastTitle action={<ToastTrigger><Link>Dismiss</Link></ToastTrigger>}>
                            {toastProps.message}
                        </ToastTitle> :
                        <ToastTitle>{toastProps.message}</ToastTitle>}
                    {toastProps.description && <ToastBody>{toastProps.description}</ToastBody>}
                </Toast>
            );
        } else {
            toastContent = (
                <Toast>
                    {toastProps.dismissable ? 
                        <ToastTitle action={<ToastTrigger><Link>Dismiss</Link></ToastTrigger>}>
                            {toastProps.message}
                        </ToastTitle> :
                        <ToastTitle>{toastProps.message}</ToastTitle>}
                    {toastProps.description && <ToastBody>{toastProps.description}</ToastBody>}
                </Toast>
            );
        }

        dispatchToast(
            toastContent,
            { 
                intent: toastProps.type === 'progress' ? 'info' : toastProps.type,
                timeout: toastProps.duration ? toastProps.duration : 4000,
                position: 'top-end'
            }   
        );
    }, [toastProps]);

    const settings = useSelector((state: RootState) => state.settings);

    const appInsights = useContext(AppInsightsContext);

    const user = useUser();

    const getAccessToken = useTokenService(); // Use the hook to get the token
    const copilotService = CopilotService.getInstance(getAccessToken);

    // Base dataset IDs are derived from the session ID, so a segment may only be exported
    // from a session the current user owns. Viewers must clone the session first, which
    // assigns a new session ID.
    const canExport = canExportFromSession({
        sessionId: settings.sessionId,
        sessionOwner: settings.sessionOwner,
        isViewingOthersSession: settings.isViewingOthersSession,
        currentUserName: user?.userProfile?.userName ?? '',
    });

    const handleScriptChange = (newScript: string) => {
        setScript(newScript);
    };

    const dispatch = useDispatch();
    const classes = useStyles();
    const titlesMapped: MappedTitle[] = [];

    const runScript = (force: boolean = true) => {
        if (
            (force && script) ||
            (script &&
                !scriptResult?.script_error &&
                !scriptResult?.executeTimeMs)
        ) {
            setScriptRunning(true);
            copilotService
                .trellisUserSegmentScriptAPI(
                    script,
                    settings.sessionId,
                    titlesMapped,
                    settings.apiEndpoint,
                    settings.sessionDataset || settings.dataset
                )
                .then((response) => {
                    // Create a new object from the response, excluding the properties you don't want
                    const newResponse = { ...response };
                    delete newResponse.script;
                    //delete newResponse.sampleUsers;
                    //setScriptResult(JSON.stringify(newResponse, null, 2)); // assuming response is a JSON object
                    setScriptResult(response); // assuming response is a JSON object

                    if (response) {
                        if(response.users == 0) {
                            showZeroCountBanner(true);
                        }
                        dispatch(
                            setScriptMessage([
                                ...settings.chat,
                                {
                                    script: script,
                                    role: 'User',
                                    endpoint: 'executeScript',
                                },
                                response,
                            ])
                        );
                    }

                    appInsights.trackEvent(
                        { name: 'SegmentExecute' },
                        {
                            user: user?.username,
                            initialPrompt: input,
                            Pseudocode: pseudocode,
                            LuaScript: script,
                            executionTimeMs: response?.executeTimeMs,
                            users: response?.users,
                            exception: response?.script_error,
                            sessionId: chatInteraction.sessionId,
                        }
                    );
                    setScriptRunning(false);
                });
        }
        else {
            if (scriptResult) {
                if (scriptResult.users == 0)
                {
                    showZeroCountBanner(true);
                }
            }
        }
    };

    const handleExportSubmit = async (exportType: ExportType, datasetName: string, description: string, isAutoRefresh: boolean, shellId?: string) => {
        // Guard the submit path as well as the button, so the ownership rule holds even if
        // the dialog is reached by some other route.
        if (!canExport) {
            setToastProps({
                type: 'error',
                message: EXPORT_DISABLED_NOT_SESSION_OWNER,
                duration: 5000,
            });
            return;
        }

        const scriptMessageIndex = messageSequenceIndex + 2;
        var exportRequestBody: ExportSegmentRequest = {
            id: settings.sessionId + "_" + scriptMessageIndex,
            dataset: getExportDataset(settings.sessionDataset, settings.dataset),
            exportType: exportType,
            datasetName: datasetName,
            description: description,
            userEmail: user?.username,
            messageIndex: scriptMessageIndex,
            chatInteraction: chatInteraction,
            chatUser: chatUser,
            chatScript: scriptResult,
            isAutoRefresh: isAutoRefresh,
            shellId: shellId,
        };

        let bdsShortname = datasetName.length > 10 ? datasetName.substring(0, 10) + '...' : datasetName;

        try {
            setToastProps({type: 'progress', message: 'Exporting base dataset...', duration: -1});

            const response = await copilotService.sendExportSegmentRequest(exportRequestBody, settings.sessionId, settings.apiEndpoint);

            if (response.Error) {
                console.error("Error in export response:", response);
                if (response.Details && response.Details.includes("id or name already exists")) {
                    setToastProps({
                        type: "info", 
                        message: "Oops! It looks like you've already submitted a request for this dataset.",
                        description: "Please check your inbox for the confirmation email.",
                        duration: 10000,
                        dismissable: true,
                    });
                } else {
                    setToastProps({
                        type: "error", 
                        message: response.Error || `Request for Base Dataset \'${bdsShortname}\' could not be submitted - please try again.`,
                        duration: -1,
                        dismissable: true,
                    });
                }
            } else {
                setToastProps({type: 'success', message: `Request for Base Dataset \'${bdsShortname}\' has been submitted.`});
            }
        } catch (error) {
            setToastProps({
                type: 'error', 
                message: `Request for Base Dataset \'${bdsShortname}\' could not be submitted - please try again.`, 
                duration: -1,
                dismissable: true,
            });
            console.error("An error occurred during the export process:", error);
        }
    }
    useEffect(() => {
        runScript(false);
    }, []);

    const LuaEditor: React.FC = () => (
        <Highlight
            theme={themes.vsLight}
            code={script ?? '--None'}
            language="lua"
        >
            {({ tokens, getLineProps, getTokenProps }) => (
                <React.Fragment>
                    {tokens.map((line, i) => (
                        <div {...getLineProps({ line, key: i })}>
                            {line.map((token, key) => (
                                <span {...getTokenProps({ token, key })} />
                            ))}
                        </div>
                    ))}
                </React.Fragment>
            )}
        </Highlight>
    );

    return (
        <div className={classes.container}>
            <div className={classes.innerContainer}>
                <Card className={classes.card}>
                    <Subtitle2Stronger className={classes.title}>
                        Results
                    </Subtitle2Stronger>
                    {!scriptResult?.script_error &&
                        isExportFeatureEnabled(settings.enableExportFeature) &&
                        <ExportPopUp
                            dataset={getExportDataset(settings.sessionDataset, settings.dataset)}
                            onSubmit={handleExportSubmit}
                            disabled={!canExport}
                            disabledReason={EXPORT_DISABLED_NOT_SESSION_OWNER}
                        />
                    }
                    <Results
                        result={scriptResult}
                        isScriptRunning={isScriptRunning}
                    />
                </Card>
            </div>
            
            <Toaster toasterId={confirmationToastId}/>
        </div>

    );
};

export default LuaScriptMessage;

// bring it back when its time
// {!scriptResult?.script_error && <Button icon={<FolderArrowRight28Regular />} onClick={togglePopup}
// className={classes.button}>Request Base Dataset</Button>}